import { useEffect } from "react"
import { matchPath, useLocation } from "react-router-dom"
import routes from "routes"
import { trackingService } from "utils/tracking"
import { TrackingEvents } from "utils/tracking/events"

const ALLOWED_ROUTES = [
  routes.home,
  routes.cpat.dashboard,
  routes.settings,
  routes.notifications,
  routes.learning,
  routes.orders,
  routes.profile.self,
  routes.aiTools.home,
  routes.masterclasses,
  "/qualification",
  "/course",
  "/certification",
]

const SMARTBANNER_CSS =
  "https://unpkg.com/smartbanner.js@1.25.0/dist/smartbanner.min.css"
const SMARTBANNER_JS =
  "https://unpkg.com/smartbanner.js@1.25.0/dist/smartbanner.min.js"

const isAllowedRoute = (pathname: string) => {
  const p = pathname.toLowerCase()
  if (matchPath(routes.assignment, pathname)) return false
  if (matchPath(routes.attemptAssessment, pathname)) return false
  if (p.includes("payment")) return false
  if (p.includes("suraasa-plus")) return false
  if (p.includes("skill-profile")) return false
  if (p.includes("attempt")) return false

  if (ALLOWED_ROUTES.some(route => p.startsWith(route.toLowerCase())))
    return true

  return false
}

export const SmartBanner = () => {
  const { pathname } = useLocation()

  const updateBanner = (p: string) => {
    const html = document.documentElement
    const banner = document.querySelector<HTMLElement>(".smartbanner")

    if (isAllowedRoute(p)) {
      html.classList.remove("smartbanner-disabled")
      if (banner) {
        banner.style.display = "block"
        html.style.marginTop = banner.offsetHeight + "px"
      }
    } else {
      html.classList.add("smartbanner-disabled")
      if (banner) {
        banner.style.display = "none"
      }
      html.style.marginTop = ""
      html.style.paddingTop = ""
    }
  }

  useEffect(() => {
    updateBanner(pathname)
  }, [pathname])

  useEffect(() => {
    if (!document.querySelector("link[data-smartbanner]")) {
      const cssLink = document.createElement("link")
      cssLink.rel = "stylesheet"
      cssLink.href = SMARTBANNER_CSS
      cssLink.dataset.smartbanner = "true"

      const customCssLink = document.createElement("link")
      customCssLink.rel = "stylesheet"
      customCssLink.href = "/SmartBannerStyling.css"
      customCssLink.dataset.smartbanner = "true"

      document.head.append(cssLink, customCssLink)
    }

    if (!document.querySelector("script[data-smartbanner]")) {
      const script = document.createElement("script")
      script.src = SMARTBANNER_JS
      script.async = true
      script.dataset.smartbanner = "true"

      script.onload = () => {
        document.addEventListener("smartbanner.view", () =>
          trackingService.trackEvent(TrackingEvents.smartbannerViewed)
        )
        document.addEventListener("smartbanner.clickout", () =>
          trackingService.trackEvent(TrackingEvents.smartbannerClicked)
        )
        document.addEventListener("smartbanner.exit", () => {
          trackingService.trackEvent(TrackingEvents.smartbannerClosed)
          if (window.location.hostname === "localhost") {
            // On localhost, clear smartbanner dismissal cookies so the banner
            // reappears on next visit — helps detect pages where it shouldn't show.
            // setTimeout(0) defers until after smartbanner.js bakes the cookie
            // (the library sets the cookie after dispatching this event).
            setTimeout(() => {
              document.cookie.split(";").forEach(cookie => {
                const name = cookie.split("=")[0].trim()
                if (name.startsWith("smartbanner")) {
                  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
                }
              })
            }, 0)
          }
        })
        updateBanner(window.location.pathname)
      }

      document.body.append(script)
    }
  }, [])

  return null
}
